import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './Dialog';
import { SecureStorage } from '../utils/secureStorage';
import { LayoutDashboard, BarChart3, Shield, HelpCircle } from 'lucide-react';

export const HeaderElements: React.FC = () => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // The hamburger toggles the same menu, so a press on it is not "outside".
      if (profileRef.current && !profileRef.current.contains(target) && !menuRef.current?.contains(target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      sessionStorage.removeItem('headerVisible');
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
      setSignOutError('An error occurred during sign out. Please try again.');

      try {
        SecureStorage.clearSensitiveData();
      } catch (storageError) {
        console.error('Error clearing storage:', storageError);
      }

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    }
  };

  const formatName = (name: string): string => {
    const deCamelCased = name.replace(/([a-z])([A-Z])/g, '$1 $2');
    const parts = deCamelCased.split(/[\s_.-]|(?=@)/);
    return parts
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ')
      .split('@')[0]
      .trim();
  };

  if (!user) {
    return null;
  }

  const displayName = profile?.full_name || formatName(user?.user_metadata?.name ||
                                                     user?.user_metadata?.full_name ||
                                                     user?.user_metadata?.preferred_username ||
                                                     user?.email?.split('@')[0] || 'User');

  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || localStorage.getItem('ms_profile_photo') || null;
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <div className="sticky top-0 bg-white backdrop-blur-xl border-b border-slate-200/60 shadow-sm" style={{ zIndex: 100 }}>
        <div className="relative flex justify-between items-center w-full px-4 sm:px-6 lg:px-8 h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2.5 group">
              <img src="/navylogonoconstruct.png" alt="HG" className="w-9 h-9 object-contain" />
              <div className="hidden sm:block w-px h-9 bg-navy/20"></div>
              <span className="text-navy font-semibold text-sm leading-tight hidden sm:block group-hover:text-orange transition-colors">
                Training<br />Platform
              </span>
            </Link>
          </div>

          {/* Nav links - centered */}
          <nav className="hidden md:flex items-center gap-2 absolute left-1/2 -translate-x-1/2">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isActive('/') ? 'bg-navy text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <LayoutDashboard size={15} />
              Dashboard
            </Link>
            <Link
              to="/my-analytics"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isActive('/my-analytics') ? 'bg-navy text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <BarChart3 size={15} />
              My Analytics
            </Link>
            <Link
              to="/faq"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isActive('/faq') ? 'bg-navy text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <HelpCircle size={15} />
              FAQ
            </Link>
            {profile?.role === 'admin' && (
              <Link
                to="/admin"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border ${
                  location.pathname.startsWith('/admin')
                    ? 'bg-navy text-white border-navy'
                    : 'text-orange border-orange/40 hover:bg-orange/5 hover:border-orange'
                }`}
              >
                <Shield size={15} />
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {/* Profile avatar with dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <span className="hidden sm:flex flex-col items-end">
                  <span className="text-navy font-bold text-sm">{displayName}</span>
                  {profile?.role === 'admin' && (
                    <span className="text-slate-400 text-[11px] font-medium leading-none">Admin</span>
                  )}
                </span>
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="w-10 h-10 rounded-full object-cover border-2 border-navy"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center text-sm font-bold border-2 border-navy">
                    {initials}
                  </div>
                )}
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg bg-white border border-gray-200 z-50 animate-scale-in">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-bold text-navy">{displayName}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <div className="py-1">
                    {/* Mobile nav links */}
                    <div className="md:hidden">
                      <Link to="/" className="block px-4 py-2 text-slate-600 hover:text-navy hover:bg-slate-50 transition-colors text-sm" onClick={() => setIsProfileOpen(false)}>
                        Dashboard
                      </Link>
                      <Link to="/my-analytics" className="block px-4 py-2 text-slate-600 hover:text-navy hover:bg-slate-50 transition-colors text-sm" onClick={() => setIsProfileOpen(false)}>
                        My Analytics
                      </Link>
                      <Link to="/faq" className="block px-4 py-2 text-slate-600 hover:text-navy hover:bg-slate-50 transition-colors text-sm" onClick={() => setIsProfileOpen(false)}>
                        FAQ
                      </Link>
                      {profile?.role === 'admin' && (
                        <Link to="/admin" className="block px-4 py-2 text-slate-600 hover:text-navy hover:bg-slate-50 transition-colors text-sm" onClick={() => setIsProfileOpen(false)}>
                          Admin Panel
                        </Link>
                      )}
                      <div className="border-t border-gray-100"></div>
                    </div>
                    <button
                      onClick={() => {
                        setIsProfileOpen(false);
                        setShowSignOutDialog(true);
                      }}
                      className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors text-sm"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Phone menu button: opens the profile menu, which carries the
                page links below md. It used to toggle a menu that was never
                drawn, so it did nothing. */}
            <div className="relative md:hidden" ref={menuRef}>
              <button
                className="text-slate-600 p-2 hover:bg-slate-100 rounded-md transition-colors"
                onClick={() => setIsProfileOpen(open => !open)}
                aria-expanded={isProfileOpen}
                aria-label="Navigation Menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSignOutDialog && (
        <Dialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Sign Out</DialogTitle>
            </DialogHeader>
            <p className="text-gray-600">Are you sure you want to sign out?</p>
            {signOutError && (
              <p className="text-red-500 text-sm mt-2">{signOutError}</p>
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={() => setShowSignOutDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleSignOut}>
                Sign Out
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
